import { Controller } from "@hotwired/stimulus"

/**
 * Video embed controller - a YouTube thumbnail that turns into the player on click
 *
 * Usage (app/views/trips/_stay_video.html.slim):
 *   data-controller="video-embed" data-video-embed-id-value="<YouTube id>" data-video-embed-title-value="<title>"
 *   a button with data-action="video-embed#play" inside
 *
 * The player comes from youtube-nocookie.com and only after the click. Before Turbo caches the page the
 * thumbnail is put back, so going back to the page doesn't start the video by itself.
 */
export default class extends Controller<HTMLElement> {
  static values = { id: String, title: String }

  declare readonly idValue: string
  declare readonly titleValue: string

  private button: HTMLButtonElement | null = null
  private iframe: HTMLIFrameElement | null = null
  private readonly restoreBound = () => this.restore()

  connect() {
    document.addEventListener("turbo:before-cache", this.restoreBound)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.restoreBound)
  }

  play() {
    const button = this.element.querySelector("button")
    if (!button || !/^[\w-]{11}$/.test(this.idValue)) return

    const iframe = document.createElement("iframe")
    iframe.src = `https://www.youtube-nocookie.com/embed/${this.idValue}?autoplay=1&rel=0`
    iframe.title = this.titleValue || "YouTube video"
    iframe.allow = "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
    iframe.allowFullscreen = true
    iframe.referrerPolicy = "strict-origin-when-cross-origin"
    iframe.className = "w-full aspect-video rounded-lg bg-black"
    iframe.tabIndex = 0

    this.button = button
    this.iframe = iframe
    button.replaceWith(iframe)
    iframe.focus()
  }

  private restore() {
    if (this.iframe && this.button) this.iframe.replaceWith(this.button)
    this.iframe = null
  }
}
